/**
 * The required interface states (MASTER_SPEC 3.3, Design Bible 13).
 *
 * Every one is a component rather than an inline branch, because the rules that
 * make them correct are easy to lose in a branch: loading is never a full-page
 * spinner and names what it waits for, empty distinguishes "filtered to
 * nothing" from "nothing exists", error names the failure and the next action,
 * a toast is never the only place a message appears, and the storefront never
 * auto-retries a payment.
 *
 * The vocabulary rules in 10.2 apply to every string here: no `Oops`, no
 * `Sorry`, no `Something went wrong`, no emoji.
 */

import { type ReactNode, useEffect, useState } from "react";
import { relativeAge } from "../lib/format.js";
import { secondsRemaining } from "../lib/retry.js";
import type { Outcome } from "../lib/transport.js";
import type { Resource } from "../lib/useResource.js";
import { SlabFrameSkeleton } from "./SlabFrame.js";
import { StatusChip } from "./primitives.js";

export function LoadingBlock({ waitingFor, cards = 3 }: { waitingFor: string; cards?: number }): ReactNode {
  return (
    <div aria-busy="true" className="stack">
      {/* 13.1: skeletons mirror the final layout; the wait is named, not spun. */}
      <p className="t-body-s t-secondary">Loading {waitingFor}…</p>
      <div className="catalog-grid">
        {Array.from({ length: cards }, (_, index) => (
          <SlabFrameSkeleton key={index} />
        ))}
      </div>
    </div>
  );
}

/** Nothing exists yet. Distinct from "your filters hid everything" below. */
export function EmptyBlock({ statement, action }: { statement: string; action?: ReactNode }): ReactNode {
  return (
    <div className="state">
      <p className="state-title t-body-l">{statement}</p>
      {action === undefined ? null : <div className="state-actions">{action}</div>}
    </div>
  );
}

export function FilteredEmptyBlock({ onClear }: { onClear: () => void }): ReactNode {
  return (
    <div className="state state--accent">
      <p className="state-title t-body-l">No items match these filters.</p>
      <p className="state-detail">
        Try a shorter title, check the SKU, or clear the filters to browse the catalogue.
      </p>
      <div className="state-actions">
        <button className="btn btn--secondary" onClick={onClear} type="button">
          Clear filters
        </button>
      </div>
    </div>
  );
}

export function ErrorBlock({
  title,
  detail,
  onRetry,
  retryLabel = "Try again",
  children
}: {
  title: string;
  detail: string;
  onRetry?: () => void;
  retryLabel?: string;
  children?: ReactNode;
}): ReactNode {
  return (
    // 9.4: errors render inline, adjacent to the thing they concern, and
    // persist. A toast is never the only place this appears.
    <div className="state state--stop" role="alert">
      <p className="state-title t-body-l">
        <StatusChip label="Stopped" tone="stop" />
        {title}
      </p>
      <p className="state-detail">{detail}</p>
      {children}
      {onRetry === undefined ? null : (
        <div className="state-actions">
          <button className="btn btn--secondary" onClick={onRetry} type="button">
            {retryLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 13.2, storefront row: customer-account-only content states the benefit and
 * links to sign-in. Never a bare 403.
 */
export function SignInBlock({ benefit }: { benefit: string }): ReactNode {
  return (
    <div className="state">
      <p className="state-title t-body-l">Sign in to see {benefit}.</p>
      <p className="state-detail">
        Your account holds your order history, your saved delivery address, and any collector tier
        the shop has assigned you. Signing in never changes a price by itself — the tier is applied
        by the server from its own records.
      </p>
      <div className="state-actions">
        <a className="btn btn--secondary" href="/account">
          Go to your account
        </a>
      </div>
    </div>
  );
}

export function NotFoundBlock({ what, onBack }: { what: string; onBack?: () => void }): ReactNode {
  return (
    <div className="state">
      <p className="state-title t-body-l">This {what} is no longer in the catalogue.</p>
      <p className="state-detail">
        Every item here is one of one, so a page disappears when the object sells or is withdrawn.
        Searching the catalogue is the fastest way to find something comparable.
      </p>
      <div className="state-actions">
        <a className="btn btn--secondary" href="/catalog">
          Search the catalogue
        </a>
        {onBack === undefined ? null : (
          <button className="btn btn--quiet" onClick={onBack} type="button">
            Back
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 13.2, storefront offline row: cached pages stay readable and purchase
 * actions state that they are offline. There is never a fake success.
 */
export function OfflineBlock({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div className="state state--stop" role="alert">
      <p className="state-title t-body-l">
        <StatusChip label="Offline" tone="stop" />
        You are offline, and this page has not loaded yet.
      </p>
      <p className="state-detail">
        This page could not be loaded. The next attempt will check for the latest details.
      </p>
      <div className="state-actions">
        <button className="btn btn--secondary" onClick={onRetry} type="button">
          Try again
        </button>
      </div>
    </div>
  );
}

export function StaleBanner({ loadedAt, onRetry }: { loadedAt: number; onRetry: () => void }): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 10_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return (
    <div className="banner banner--stop">
      <StatusChip label="Offline" tone="stop" />
      <span>
        Showing the page loaded {relativeAge(loadedAt, now)}. Prices and availability may have
        changed, so buying is switched off until this reloads.
      </span>
      <button className="btn btn--quiet" onClick={onRetry} type="button">
        Try again
      </button>
    </div>
  );
}

export function RetryingBlock({
  attempt,
  maxAttempts,
  nextAttemptAt,
  onRetryNow
}: {
  attempt: number;
  maxAttempts: number;
  nextAttemptAt: number;
  onRetryNow: () => void;
}): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [nextAttemptAt]);
  const seconds = secondsRemaining(nextAttemptAt, now);
  return (
    <div className="state state--accent">
      {/*
       * 9.4: the countdown ticks visibly but is not inside a live region. Only
       * the attempt line announces, so a screen reader is not flooded once per
       * second. 8.4: nothing here rotates, so removing motion removes nothing.
       */}
      <p className="state-title t-body-l">
        <StatusChip label="Retrying" tone="accent" />
        Attempt {attempt} of {maxAttempts}
      </p>
      <p aria-atomic="true" aria-live="polite" className="u-visually-hidden">
        Retrying. Attempt {attempt} of {maxAttempts}.
      </p>
      <p className="state-detail">
        We could not load this yet. Trying again in <span className="t-id">{seconds}s</span>.
      </p>
      <div className="state-actions">
        <button className="btn btn--secondary" onClick={onRetryNow} type="button">
          Try now
        </button>
      </div>
    </div>
  );
}

/**
 * 13.1: success is proportional. Anything involving money or a provider side
 * effect is specific and persists.
 */
export function SuccessBlock({
  title,
  detail,
  children
}: {
  title: string;
  detail: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="state state--ok">
      <p className="state-title t-body-l">
        <StatusChip label="Confirmed" tone="ok" />
        {title}
      </p>
      <p className="state-detail">{detail}</p>
      {children}
    </div>
  );
}

/**
 * The commerce capability gate (WP04 stop condition). This is deliberately not
 * an error: nothing is broken, the operator has not switched checkout on. 14
 * bans simulating a control that does not work, so no disabled `Buy now`
 * appears anywhere alongside this — the rule is stated instead.
 */
export function CheckoutUnavailableBlock({ reason, inquiryHref = "/help" }: { reason: string; inquiryHref?: string }): ReactNode {
  return (
    <div className="state state--accent">
      <p className="state-title t-body-l">
        <StatusChip label="Checkout is off" tone="accent" />
        Buying directly on this site is not available.
      </p>
      <p className="state-detail">{reason}</p>
      <p className="state-detail">
        You can still inspect the photographs and ask about an item. Items listed on eBay can be
        purchased through their eBay link.
      </p>
      <div className="state-actions">
        <a className="btn btn--secondary" href={inquiryHref}>
          Ask about an item
        </a>
      </div>
    </div>
  );
}

export interface ResourceGateProps<T> {
  readonly resource: Resource<T>;
  /** Named in the loading state, e.g. "the catalogue". */
  readonly waitingFor: string;
  /** Named in the not-found state, e.g. "item". */
  readonly subject: string;
  readonly skeletonCards?: number;
  readonly children: (value: T, stale: boolean) => ReactNode;
}

/** Maps a `Resource` onto the state blocks above. Views never branch on phases. */
export function ResourceGate<T>({
  resource,
  waitingFor,
  subject,
  skeletonCards,
  children
}: ResourceGateProps<T>): ReactNode {
  const { state, value, loadedAt } = resource;

  if (state.phase === "ready" && value !== null) return children(value, false);
  if (state.phase === "loading") {
    return <LoadingBlock cards={skeletonCards ?? 3} waitingFor={waitingFor} />;
  }
  if (state.phase === "retrying") {
    return (
      <div className="stack">
        <RetryingBlock
          attempt={state.attempt}
          maxAttempts={state.maxAttempts}
          nextAttemptAt={state.nextAttemptAt}
          onRetryNow={resource.retryNow}
        />
        {value !== null && loadedAt !== null ? children(value, true) : null}
      </div>
    );
  }
  if (state.phase === "failed") {
    return renderFailure(state.outcome, resource, subject, value, loadedAt, children);
  }
  return <LoadingBlock cards={skeletonCards ?? 3} waitingFor={waitingFor} />;
}

function renderFailure<T>(
  outcome: Outcome<never>,
  resource: Resource<T>,
  subject: string,
  value: T | null,
  loadedAt: number | null,
  children: (value: T, stale: boolean) => ReactNode
): ReactNode {
  if (outcome.kind === "not-found") return <NotFoundBlock what={subject} />;
  if (outcome.kind === "denied") return <SignInBlock benefit={subject} />;
  if (outcome.kind === "disabled") {
    return (
      <CheckoutUnavailableBlock reason="The server reports that this part of the shop is switched off right now." />
    );
  }
  if (outcome.kind === "offline") {
    if (value !== null && loadedAt !== null) {
      return (
        <div className="stack">
          <StaleBanner loadedAt={loadedAt} onRetry={resource.retryNow} />
          {children(value, true)}
        </div>
      );
    }
    return <OfflineBlock onRetry={resource.retryNow} />;
  }
  if (outcome.kind === "unavailable") {
    return (
      <ErrorBlock
        detail={`The server answered ${String(outcome.status)} after automatic retries stopped. Check again to load the latest details.`}
        onRetry={resource.retryNow}
        title={`The ${subject} could not be loaded.`}
      />
    );
  }
  if (outcome.kind === "malformed") {
    return (
      <ErrorBlock
        detail={`The response did not match the public catalogue contract: ${outcome.detail}. Showing nothing is deliberate — a partly-understood record is not something to sell from.`}
        onRetry={resource.retryNow}
        title={`The ${subject} arrived in a form this page will not display.`}
      />
    );
  }
  if (outcome.kind === "rate-limited") {
    return (
      <ErrorBlock
        detail="Too many requests came from this connection. Waiting a minute and trying again works."
        onRetry={resource.retryNow}
        title="This request was rate limited."
      />
    );
  }
  return (
    <ErrorBlock
      detail={
        outcome.kind === "rejected" || outcome.kind === "conflict"
          ? outcome.detail
          : "No reason was given."
      }
      onRetry={resource.retryNow}
      title={`The request for the ${subject} was rejected.`}
    />
  );
}
